import { StudentTab } from "./StudentTab";
import { Datagrid, FieldProps, Pagination, useGetOne, useGetRecordId, useRecordContext } from "react-admin";
import { Typography } from "@mui/material";
import { ResponsiveListSurface } from "../../misc/ResponsiveListSurface";
import { DateField } from "../../fields/DateField";
import { LinkToFirestoreField } from "../../fields/LinkToFirestoreField";
import { ReferenceManyField } from "../../fields/ReferenceManyField";
import {
  AutovioCalendarEvent,
  getASFCourseSessionAttendanceState,
  getTheoryLessonAttendanceState,
  isTheoryExam,
} from "../../model/autovioCalendarEvents";
import { grants } from "../../backoffice.access_control";
import { InstructorField } from "../../fields/InstructorField";
import { useEventDialog } from "../../hooks/useEventDialog";
import { CalendarEventStatusField } from "../../fields/CalendarEventStatusField";
import { Row } from "../../misc/Row";
import { DocumentHistoricalAppointmentButton } from "../../buttons/DocumentHistoricalAppointmentButton";
import { Student } from "../../providers/studentsProvider";
import { getTheoryExamAttempt } from "../../utils/examsAttempt";
import { l10n } from "../../l10n";
import { EmptyState } from "../../misc/EmptyState";
import { TheoryEvent } from "../../providers/theoryEventsProvider";
import { DateTime } from "luxon";

/**
 * Shows the theory lessons and theory exams of a student.
 */
export function StudentTheoryTab() {
  const studentId = useGetRecordId() as string;
  const { eventDialog, openEventDialog } = useEventDialog({ syncWithUrl: true });

  return (
    <StudentTab>
      <>
        <Row
          sx={{
            flexDirection: { xs: "column", sm: "row" },
            flexWrap: "wrap",
            justifyContent: "space-between",
            alignItems: { xs: "stretch", sm: "center" },
            gap: "12px 16px",
            marginBottom: "18px",
            "& > .MuiButton-root": { width: { xs: "100%", sm: "auto" } },
          }}
        >
          <Typography sx={{ fontWeight: "bold", fontSize: 19 }}>Theorietermine</Typography>
          {(grants.has("TheoryLesson:historical:create") || grants.has("Exam:historical:create")) && (
            <DocumentHistoricalAppointmentButton tabs={["theoryLesson", "theoryExam"]} />
          )}
        </Row>
        <ResponsiveListSurface
          variant="table"
          sx={{ "& .RaDatagrid-table": { minWidth: "680px", backgroundColor: "background.paper" } }}
        >
          <ReferenceManyField
            label=""
            pagination={<Pagination />}
            reference="theoryEvents"
            target="studentUids"
            sort={{ field: "start", order: "DESC" }}
          >
            <Datagrid
              bulkActionButtons={false}
              empty={<EmptyState noEntriesText="Noch kein Theorieunterricht gebucht." />}
              rowClick={(_, __, theoryEvent) => {
                openEventDialog(theoryEvent as AutovioCalendarEvent);
                return false;
              }}
            >
              <DateField label="Datum" source="start" showTime />
              <_TypeField label="Art" />
              <InstructorField label="Fahrlehrer" source="instructorId" />
              <_StatusField label="Status" studentId={studentId} />
              {grants.has("viewFirestore") && (
                <LinkToFirestoreField label="" source="id" collection="/calendar_events" />
              )}
            </Datagrid>
            {eventDialog}
          </ReferenceManyField>
        </ResponsiveListSurface>
      </>
    </StudentTab>
  );
}

function _TypeField(_props: FieldProps) {
  const record = useRecordContext<TheoryEvent>();
  const { data: student } = useGetOne<Student>(
    "students",
    { id: isTheoryExam(record) ? record.student.uid : "" },
    { enabled: isTheoryExam(record) },
  );
  if (record.type === "TheoryLesson") {
    return <Typography variant="body2">{l10n.lectureXxx(record)}</Typography>;
  } else if (record.type === "TheoryExam") {
    const attemptNumber = student ? getTheoryExamAttempt(record, student) : undefined;
    return <Typography variant="body2">{`${attemptNumber ? `${attemptNumber}. ` : ""}Theorieprüfung`}</Typography>;
  } else if (record.type === "ASFCourseSession") {
    return <Typography variant="body2">{`ASF Kurs Sitzung ${record.sessionType.substring(7)}`}</Typography>;
  }
  return null;
}

function _StatusField(props: FieldProps & { studentId: string }) {
  const { studentId } = props;
  const record = useRecordContext<TheoryEvent>();
  if (record.type === "TheoryLesson") {
    return (
      <Typography variant="body2">
        {(() => {
          try {
            switch (getTheoryLessonAttendanceState(studentId, record)) {
              case "attended":
                return "✔️ teilgenommen";
              case "no-show":
                return "❌ nicht erschienen";
              case "cancelled":
                return "❌ abgesagt";
              case "booked":
                return "⏳️ gebucht";
            }
          } catch (error) {
            console.warn(error);
            return "? Unbekannt";
          }
        })()}
      </Typography>
    );
  } else if (record.type === "ASFCourseSession") {
    return (
      <Typography variant="body2">
        {(() => {
          switch (getASFCourseSessionAttendanceState(studentId, record)) {
            case "attended":
              return "✔️ teilgenommen";
            case "no-show":
              return "❌ nicht erschienen";
            default:
              return record.end < DateTime.now() ? "? Unbekannt" : "";
          }
        })()}
      </Typography>
    );
  } else {
    return <CalendarEventStatusField />;
  }
}
